import { useState, useCallback, useRef } from 'react';
import { eventNotificationService, NotificationCategory } from '../services/eventNotificationService';
import { NotificationData } from '../types/notification';

const LIMIT = 20;

interface UseNotificationsReturn {
  notifications:            NotificationData[];
  total:                    number;
  isLoading:                boolean;
  isLoadingMore:            boolean;
  error:                    string | null;
  hasMore:                  boolean;
  fetch:                    (category?: NotificationCategory) => Promise<void>;
  loadMore:                 () => Promise<void>;
  optimisticMarkAsRead:     (id: number) => (() => void);
  optimisticMarkAllAsRead:  () => (() => void);
  optimisticDelete:         (id: number) => (() => void);
  optimisticDeleteRead:     () => (() => void);
  /** toggle は常に「別タブへ移動」なので現在リストから除去する */
  optimisticToggleHandled:  (id: number) => (() => void);
  optimisticDeleteHandled:  () => (() => void);
}

export const useNotifications = (): UseNotificationsReturn => {
  const [notifications, setNotifications] = useState<NotificationData[]>([]);
  const [total,         setTotal]         = useState(0);
  const [page,          setPage]          = useState(1);
  const [isLoading,     setIsLoading]     = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error,         setError]         = useState<string | null>(null);

  // 現在取得中のカテゴリを ref で保持（loadMore で参照）
  const categoryRef = useRef<NotificationCategory>('student');

  const fetch = useCallback(async (category: NotificationCategory = 'student') => {
    categoryRef.current = category;
    setIsLoading(true);
    setError(null);
    try {
      const data = await eventNotificationService.getNotifications({ category, page: 1, limit: LIMIT });
      setNotifications(data.notifications);
      setTotal(data.pagination.total);
      setPage(1);
    } catch {
      setError('通知の取得に失敗しました');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadMore = useCallback(async () => {
    setIsLoadingMore(true);
    try {
      const nextPage = page + 1;
      const data = await eventNotificationService.getNotifications({
        category: categoryRef.current,
        page:     nextPage,
        limit:    LIMIT,
      });
      setNotifications((prev) => [...prev, ...data.notifications]);
      setTotal(data.pagination.total);
      setPage(nextPage);
    } catch {
      // サイレント処理
    } finally {
      setIsLoadingMore(false);
    }
  }, [page]);

  /** 楽観的更新：個別既読 */
  const optimisticMarkAsRead = useCallback((id: number) => {
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, is_read: true } : n)));
    return () => setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, is_read: false } : n)));
  }, []);

  /** 楽観的更新：全件既読 */
  const optimisticMarkAllAsRead = useCallback(() => {
    const original = notifications.map((n) => ({ ...n }));
    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
    return () => setNotifications(original);
  }, [notifications]);

  /** 楽観的更新：個別削除 */
  const optimisticDelete = useCallback((id: number) => {
    const original      = notifications.map((n) => ({ ...n }));
    const originalTotal = total;
    setNotifications((prev) => prev.filter((n) => n.id !== id));
    setTotal((prev) => prev - 1);
    return () => {
      setNotifications(original);
      setTotal(originalTotal);
    };
  }, [notifications, total]);

  /** 楽観的更新：既読通知を一括削除（カテゴリ内の既読を全除去） */
  const optimisticDeleteRead = useCallback(() => {
    const original      = notifications.map((n) => ({ ...n }));
    const originalTotal = total;
    const readCount     = notifications.filter((n) => n.is_read).length;
    setNotifications((prev) => prev.filter((n) => !n.is_read));
    setTotal((prev) => prev - readCount);
    return () => {
      setNotifications(original);
      setTotal(originalTotal);
    };
  }, [notifications, total]);

  /**
   * 楽観的更新：対応済みトグル
   * toggle は常に「別タブへ移動」を意味するため、現在リストから除去する。
   * （student/company タブ → handled タブへ、handled タブ → student/company タブへ）
   */
  const optimisticToggleHandled = useCallback((id: number) => {
    const original      = notifications.map((n) => ({ ...n }));
    const originalTotal = total;
    setNotifications((prev) => prev.filter((n) => n.id !== id));
    setTotal((prev) => Math.max(0, prev - 1));
    return () => {
      setNotifications(original);
      setTotal(originalTotal);
    };
  }, [notifications, total]);

  /** 楽観的更新：対応済み通知を一括削除（handled タブの全件を除去） */
  const optimisticDeleteHandled = useCallback(() => {
    const original = notifications.map((n) => ({ ...n }));
    setNotifications([]);
    setTotal(0);
    return () => setNotifications(original);
  }, [notifications]);

  const hasMore = notifications.length < total;

  return {
    notifications,
    total,
    isLoading,
    isLoadingMore,
    error,
    hasMore,
    fetch,
    loadMore,
    optimisticMarkAsRead,
    optimisticMarkAllAsRead,
    optimisticDelete,
    optimisticDeleteRead,
    optimisticToggleHandled,
    optimisticDeleteHandled,
  };
};
